import React, { useMemo } from 'react';
import { COLORS, calculateSegmentAngle } from '../utils/wheelUtils';

const Wheel = ({ entries, rotation, isSpinning, size = 320 }) => {
  const segmentAngle = calculateSegmentAngle(entries.length);

  const segments = useMemo(() => {
    // A single entry can't be drawn as a pie arc — a 360deg arc has an
    // identical start/end point, so the SVG path degenerates to nothing.
    // Render it as a plain filled circle instead.
    if (entries.length <= 1) return null;

    return entries.map((entry, i) => {
      const startAngle = i * segmentAngle - 90; // start from top
      const endAngle = startAngle + segmentAngle;
      const largeArc = segmentAngle > 180 ? 1 : 0;

      const rad = (deg) => (deg * Math.PI) / 180;
      const r = size / 2 - 4;
      const cx = size / 2;
      const cy = size / 2;

      const x1 = cx + r * Math.cos(rad(startAngle));
      const y1 = cy + r * Math.sin(rad(startAngle));
      const x2 = cx + r * Math.cos(rad(endAngle));
      const y2 = cy + r * Math.sin(rad(endAngle));

      const path = [
        `M ${cx} ${cy}`,
        `L ${x1} ${y1}`,
        `A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`,
        'Z',
      ].join(' ');

      // text position (middle of segment)
      const midAngle = startAngle + segmentAngle / 2;
      const textR = r * 0.62;
      const tx = cx + textR * Math.cos(rad(midAngle));
      const ty = cy + textR * Math.sin(rad(midAngle));

      return {
        path,
        color: COLORS[i % COLORS.length],
        text: entry.text,
        tx,
        ty,
        midAngle,
      };
    });
  }, [entries, segmentAngle, size]);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {/* Pointer (fixed) — points left, into the wheel */}
      <div
        className="absolute z-20"
        style={{
          right: -8,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <div
          className="w-0 h-0"
          style={{
            borderTop: '14px solid transparent',
            borderBottom: '14px solid transparent',
            borderRight: '24px solid #FBBF24',
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
          }}
        />
      </div>

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="rounded-full shadow-2xl"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: isSpinning
            ? 'transform 5.5s cubic-bezier(0.15, 0.85, 0.25, 1)'
            : 'none',
        }}
      >
        {entries.length === 0 ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size / 2 - 4}
            fill="none"
            stroke="#334155"
            strokeWidth="2"
            strokeDasharray="6 6"
          />
        ) : entries.length === 1 ? (
          <g>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={size / 2 - 4}
              fill={COLORS[0]}
              stroke="#0f172a"
              strokeWidth="1.5"
            />
            <text
              x={size / 2}
              y={size / 2}
              fill="#fff"
              fontSize={15}
              fontWeight="600"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {entries[0].text.length > 12
                ? `${entries[0].text.slice(0, 11)}…`
                : entries[0].text}
            </text>
          </g>
        ) : (
          segments.map((seg, i) => (
            <g key={i}>
              <path d={seg.path} fill={seg.color} stroke="#0f172a" strokeWidth="1.5" />
              <text
                x={seg.tx}
                y={seg.ty}
                fill="#fff"
                fontSize={entries.length > 20 ? 11 : entries.length > 12 ? 13 : 15}
                fontWeight="600"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${seg.midAngle + 90}, ${seg.tx}, ${seg.ty})`}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {seg.text.length > 12 ? `${seg.text.slice(0, 11)}…` : seg.text}
              </text>
            </g>
          ))
        )}

        {/* Center circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size * 0.09}
          fill="#f8fafc"
          stroke="#0f172a"
          strokeWidth="3"
        />
      </svg>
    </div>
  );
};

export default Wheel;